import { useEffect, useState } from "react"
import { toast } from "sonner"
import { useUpdateProfileName } from "@/lib/queries"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

// Asks the person for the name that will appear on their certificate of
// completion.
//
// `required` drives whether it can be dismissed. Before a first assessment it
// is true: a certificate is an accreditation record, and one naming a login
// handle instead of a person is not usable as evidence — so there is no
// cancel, no escape, and no overlay click-through. Reached from the training
// page to fix a typo, it is false and behaves like any other dialog.
export function FullNameDialog({
  open,
  required = false,
  initialValue = "",
  onOpenChange,
  onSaved,
}: {
  open: boolean
  required?: boolean
  initialValue?: string
  onOpenChange?: (open: boolean) => void
  onSaved?: () => void
}) {
  const [name, setName] = useState(initialValue)
  const updateName = useUpdateProfileName()

  // Reseed whenever it reopens, so an abandoned edit does not persist into
  // the next time the person opens it.
  useEffect(() => {
    if (open) setName(initialValue)
  }, [open, initialValue])

  const trimmed = name.trim()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!trimmed) return
    try {
      await updateName.mutateAsync(trimmed)
      onSaved?.()
      onOpenChange?.(false)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not save your name"
      )
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (required && !next) return
        onOpenChange?.(next)
      }}
    >
      <DialogContent
        hideClose={required}
        onEscapeKeyDown={(e) => required && e.preventDefault()}
        onPointerDownOutside={(e) => required && e.preventDefault()}
        onInteractOutside={(e) => required && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {required ? "Before you begin" : "Name on your certificates"}
          </DialogTitle>
          <DialogDescription>
            {required
              ? "We need your full name so it can appear on your certificate of completion."
              : "This is the name printed on your certificates of completion."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full-name">Full name</Label>
            <Input
              id="full-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane A. Smith"
              autoFocus
              required
            />
          </div>
          <DialogFooter>
            {!required && (
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange?.(false)}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={!trimmed || updateName.isPending}>
              {updateName.isPending
                ? "Saving…"
                : required
                  ? "Continue"
                  : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
